/**
 * Cache del informe.
 *
 * Guarda un unico juego de datos por subcuenta: el del rango mas amplio que se
 * haya cargado. Como los rangos estan anidados, cualquier ventana mas corta se
 * obtiene recortando en memoria, sin volver a llamar al API. Asi, moverse entre
 * los botones Semana / Mes / 3 meses / Total solo cuesta peticiones cuando se
 * amplia la ventana; volver hacia atras es instantaneo.
 *
 * Vive en memoria y, si entra, tambien en sessionStorage para sobrevivir a un
 * F5. Una subcuenta grande no cabe en sessionStorage: en ese caso se descarta
 * la copia persistente sin romper nada.
 */
import { cubre, cutoffDe } from './ranges.js'

const CLAVE = 'ghl-nogestionados:datos'
const TTL_MS = 30 * 60 * 1000
const MAX_BYTES = 3.5 * 1024 * 1024

const memoria = new Map()

/** Datos servibles para ese rango, o null si hay que ir al API. */
export function leerCache(locationId, range, now = Date.now()) {
  const entrada = memoria.get(locationId) || rehidratar(locationId, now)
  if (!entrada) return null
  if (now - entrada.loadedAt > TTL_MS) return null
  if (!cubre(entrada.range, range)) return null

  const cutoff = cutoffDe(range, now)
  return {
    ...recortar(entrada, cutoff),
    loadedAt: entrada.loadedAt,
    rangeCargado: entrada.range,
    derivado: entrada.range !== range,
  }
}

/** Guarda el resultado si amplia lo que ya habia. */
export function guardarCache(locationId, range, datos, now = Date.now()) {
  const previo = memoria.get(locationId)
  if (previo && cubre(previo.range, range) && previo.range !== range) return

  const entrada = {
    range,
    loadedAt: now,
    pipelines: datos.pipelines,
    contacts: datos.contacts,
    opportunities: datos.opportunities,
  }
  memoria.set(locationId, entrada)
  persistir(locationId, entrada)
}

export function limpiarCache(locationId) {
  memoria.delete(locationId)
  try { sessionStorage.removeItem(`${CLAVE}:${locationId}`) } catch { /* modo privado */ }
}

/** Rango mas amplio disponible en cache, para pintar que botones son gratis. */
export function rangoEnCache(locationId, now = Date.now()) {
  const entrada = memoria.get(locationId) || rehidratar(locationId, now)
  if (!entrada || now - entrada.loadedAt > TTL_MS) return null
  return entrada.range
}

function recortar(entrada, cutoff) {
  if (cutoff === null) {
    return { pipelines: entrada.pipelines, contacts: entrada.contacts, opportunities: entrada.opportunities }
  }
  const dentro = (v) => {
    if (!v) return true
    const t = typeof v === 'number' ? v : Date.parse(v)
    return Number.isNaN(t) ? true : t >= cutoff
  }
  return {
    pipelines: entrada.pipelines,
    contacts: entrada.contacts.filter((c) => dentro(c.createdAt)),
    opportunities: entrada.opportunities.filter((o) => dentro(o.createdAt)),
  }
}

/**
 * Registros por encima de los cuales ni se intenta serializar: un barrido
 * historico son ~100.000 fichas y solo el JSON.stringify para descubrir que no
 * caben bloquea el hilo varios segundos. Con ~350 bytes por registro, 10.000
 * quedan holgadamente por debajo del limite.
 */
const MAX_REGISTROS = 10000

function persistir(locationId, entrada) {
  const registros = entrada.contacts.length + entrada.opportunities.length
  if (registros > MAX_REGISTROS) {
    try { sessionStorage.removeItem(`${CLAVE}:${locationId}`) } catch { /* nada que hacer */ }
    return
  }
  try {
    const json = JSON.stringify(entrada)
    if (json.length > MAX_BYTES) {
      sessionStorage.removeItem(`${CLAVE}:${locationId}`)
      return
    }
    sessionStorage.setItem(`${CLAVE}:${locationId}`, json)
  } catch {
    // Cuota superada o almacenamiento bloqueado: la cache en memoria basta.
    try { sessionStorage.removeItem(`${CLAVE}:${locationId}`) } catch { /* nada que hacer */ }
  }
}

function rehidratar(locationId, now) {
  try {
    const json = sessionStorage.getItem(`${CLAVE}:${locationId}`)
    if (!json) return null
    const entrada = JSON.parse(json)
    if (!entrada?.loadedAt || now - entrada.loadedAt > TTL_MS) return null
    memoria.set(locationId, entrada)
    return entrada
  } catch {
    return null
  }
}
